// doc-state.service.ts
import { Injectable, computed, effect, inject, signal, WritableSignal } from '@angular/core';
import { FilesService } from './files.service';
import { DocDataService } from '../app/doc-data.service';
import { FileCategory, FileTemplate } from '@/models/binder-file';
import { sha256 } from 'js-sha256';
import { BinderService } from './binder.service';
import { ObjectSnapshotService } from '../app/object-snapshot.service';

@Injectable({ providedIn: 'root' })
export class DocStateService {
  private filesService = inject(FilesService);
  private docDataService = inject(DocDataService);
  private binderService = inject(BinderService);
  private objectSnapshotService = inject(ObjectSnapshotService);

  stateUpdateCatIds: number[] = [1];

  constructor() {
    effect(() => {
      const categories = this.filesService.categories();
      const binder = this.binderService.currentBinder();
  
      if (!categories.length || !binder) return;
  
      const selected = categories.map((cat) => {
        let templateId: number | null = null;
  
        if (this.stateUpdateCatIds.includes(cat.fileCategoryId)) {
          const match = cat.fileTemplates.find(t => t.StateID === binder.StateID && !t.hidden);
          templateId = match?.fileTemplateId ?? null;
        }
  
        if (templateId == null) {
          const lastFile = this.filesService.getMostRecentFile(cat.fileCategoryId);
          templateId = lastFile?.fileTemplateId ?? cat.fileTemplates.find(t => !t.hidden)?.fileTemplateId ?? null;
        }
  
        return {
          fileCategoryId: cat.fileCategoryId,
          selectedTemplateId: templateId,
        };
      });
  
      this.selectedTemplateIds.set(selected);
    });

    effect(() => {
      const regen = this.templatesNeedingRegeneration();
      if(regen.length > 0) {
        for(const template of regen) {
          this.filesService.selectCategoryForGeneration(template.fileCategoryId);
        }
      }
    });
  }

  selectedTemplateIds: WritableSignal<{
    fileCategoryId: number;
    selectedTemplateId: number | null;
  }[]> = signal([]);

  lastUsedTemplateIds = computed(() =>
    this.categories.map(cat => {
      const latestFile = [...(cat.binderFiles ?? [])].sort((a, b) =>
        new Date(b.modifiedOn || b.createdOn).getTime() - new Date(a.modifiedOn || a.createdOn).getTime()
      )[0];

      return {
        fileCategoryId: cat.fileCategoryId,
        lastUsedTemplateId: latestFile?.fileTemplateId ?? null,
        lastDocHash: latestFile?.docHash ?? null
      };
    })
  );

  templatesNeedingRegeneration = computed(() => {
    const selected = this.selectedTemplateIds();
    const lastUsed = this.lastUsedTemplateIds();
    const data = this.docDataService.jsonData();
    const categories = this.filesService.categories();

    return selected.filter(sel => {
      const last = lastUsed.find(lu => lu.fileCategoryId === sel.fileCategoryId);
      const cat = this.getFilteredCategories(categories).find(c => c.fileCategoryId === sel.fileCategoryId);
      const selectedTemplate = cat?.fileTemplates.find(t => t.fileTemplateId === sel.selectedTemplateId);

      if (!selectedTemplate || !data) return false;

      const currentHash = this.objectSnapshotService.getSnapshotHash(data, selectedTemplate.schemaString);

      return sel.selectedTemplateId !== last?.lastUsedTemplateId || currentHash !== last?.lastDocHash;
    });
  });

  updateSelectedTemplate(fileCategoryId: number, templateId: number | null): void {
    const updated = this.selectedTemplateIds().map(entry =>
      entry.fileCategoryId === fileCategoryId ? { ...entry, selectedTemplateId: templateId } : entry
    );
    this.selectedTemplateIds.set(updated);
  }

  get categories() {
    if(this.binderService.currentBinder()?.enableExtraTemplates) {
      return this.filesService.categories();
    }
    return this.getFilteredCategories(this.filesService.categories());
  }

  getFilteredCategories(categories: FileCategory[]) {
    return categories.filter(cat => !cat.hidden);
  }

  getSelectedTemplate(fileCategoryId: number): FileTemplate | undefined {
    const selectedEntry = this.selectedTemplateIds().find(
      (entry) => entry.fileCategoryId === fileCategoryId
    );
  
    if (!selectedEntry?.selectedTemplateId) return undefined;
  
    const category = this.categories.find(
      (cat) => cat.fileCategoryId === fileCategoryId
    );
  
    return category?.fileTemplates.find(
      (tpl) => tpl.fileTemplateId === selectedEntry.selectedTemplateId
    );
  }

  getTempalteById(fileCategoryId: number, templateId: number | null): FileTemplate | null {
    if(!fileCategoryId || !templateId) return null;
    const category = this.categories.find(
      (cat) => cat.fileCategoryId === fileCategoryId
    );
    return category?.fileTemplates.find(
      (tpl) => tpl.fileTemplateId === templateId
    ) ?? null;
  }

  // generateHashForTemplate(template: FileTemplate, jsonData: any): string {
  //   try {
  //     console.log('generating hash for template', template);
  //     const schema = JSON.parse(template.tempSchema);


  //     console.log('relevant data', relevantData);
  //     return sha256(JSON.stringify(relevantData));
  //   } catch (e) {
  //     console.error(`Hash generation failed for template ${template.templateName}`, e);
  //     return '';
  //   }
  // }
}